import { ArrowDown, ArrowUp, Crosshair, Download, FileDown, Loader2, Radar } from "lucide-react";
import type { ReactNode } from "react";

import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Card, CardContent } from "@/shared/ui/card";

import type { GeoReport } from "../../../api/geoService";
import { rankWithBrand } from "../../../lib/competitorRank";
import { formatScoreDelta, roundDelta } from "../../../lib/scoreDelta";
import { scoreVersionLabel } from "../../../lib/scoreVersionLabel";

function fmtTime(iso?: string): string {
  if (!iso) return "";
  const m = iso.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/);
  return m ? `${m[1]} ${m[2]}` : iso;
}

/**
 * Hero 里的小徽标/标签。
 *
 * ★1005 上线前走查:原来整条 Hero 写死墨蓝底 + 白字(hsl(213 70% 12%) 一族),但 Hero 用的是
 * <Card>(带 .rounded-lg.border.bg-card),三套皮肤都有
 * `[data-skin] :is(.rounded-lg,.rounded-xl).border.bg-card { background: hsl(var(--card)) !important }`,
 * 内联墨蓝底被盖成浅色卡片底 → 白字的「导出 PDF / 基线轮」和 72px 总分大字几乎看不见。
 * 现在 Hero 整体回到主题 token(卡片底 + card-foreground / muted-foreground / primary / success),
 * 三套皮肤各自成立,不再写死颜色。打印样式仍按 [data-print-hero] 另行收口(index.css @media print)。
 */
function HeroPill({ children }: { children: ReactNode }) {
  return (
    <Badge
      variant="secondary"
      className="rounded-full px-3 py-1 text-2xs font-medium normal-case tracking-normal"
    >
      {children}
    </Badge>
  );
}

interface HeroActionsProps {
  isBaseline?: boolean;
  onExportHtml?: () => void;
  onExportPdf?: () => void;
  onRepointBaseline?: () => void;
  repointing?: boolean;
}

/** 右上角动作组:导出 HTML(主按钮)/ 导出 PDF / 设为基线——导出副本里整组隐藏。 */
function HeroActions({
  isBaseline,
  onExportHtml,
  onExportPdf,
  onRepointBaseline,
  repointing,
}: HeroActionsProps) {
  if (!onExportHtml && !onExportPdf && isBaseline == null && !onRepointBaseline) return null;
  return (
    <div className="relative z-10 mb-6 flex flex-wrap justify-end gap-2" data-geo-export-hide>
      {onExportHtml && (
        <Button size="sm" data-testid="geo-export-html" onClick={onExportHtml}>
          <FileDown className="h-3.5 w-3.5" /> 导出 HTML
        </Button>
      )}
      {onExportPdf && (
        <Button size="sm" variant="outline" data-testid="geo-export-pdf" onClick={onExportPdf}>
          <Download className="h-3.5 w-3.5" /> 导出 PDF
        </Button>
      )}
      {isBaseline ? (
        <Badge
          variant="outline"
          data-testid="geo-baseline-badge"
          className="rounded-md px-3.5 py-1.5 text-[13.5px] font-medium normal-case tracking-normal"
        >
          基线轮
        </Badge>
      ) : (
        onRepointBaseline && (
          <Button
            size="sm"
            variant="outline"
            data-testid="geo-repoint-baseline"
            disabled={repointing}
            onClick={onRepointBaseline}
          >
            {repointing ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Crosshair className="h-3.5 w-3.5" />
            )}
            设为基线
          </Button>
        )
      )}
    </div>
  );
}

interface HeroKpi {
  id: string;
  label: string;
  value: string;
  sub: string;
  warn?: boolean;
}

function HeroKpiCard({ kpi }: { kpi: HeroKpi }) {
  return (
    <Card data-testid={`geo-kpi-${kpi.id}`}>
      <CardContent className="flex flex-col gap-1.5 p-5">
        <span className="text-2xs text-muted-foreground">{kpi.label}</span>
        <span
          className={`text-[28px] font-extrabold leading-none tabular-nums ${
            kpi.warn ? "text-amber-600" : ""
          }`}
        >
          {kpi.value}
        </span>
        <span className="text-2xs leading-relaxed text-muted-foreground">{kpi.sub}</span>
      </CardContent>
    </Card>
  );
}

/**
 * 竞品位次 KPI:把「本品牌」并进 competitor_ranking 的同一份排行(口径见 `rankWithBrand`
 * 顶部注释),推出一个 N/M 位次。任一侧数据缺席时 `rankWithBrand` 返回 null,这里跟着不渲染。
 */
function computeCompetitorRankKpi(report: GeoReport): HeroKpi | null {
  // 1005:优先用 ai 下发的同池位次(与 01 提及率同口径)。旧口径把「问句里本就带品牌名」的命中也算进排行,
  // 生产出现过「提及 0 次却排 11/93 位」。
  const br = report.search_overview?.brand_rank;
  if (br) {
    return br.position == null
      ? {
          id: "rank",
          label: "竞品可见度位次",
          value: "未被提及",
          sub: `${br.total} 个同台品牌中,本品牌未被 AI 主动提及`,
        }
      : {
          id: "rank",
          label: "竞品可见度位次",
          value: `${br.position} / ${br.total}`,
          sub: `在 ${br.total} 个同台品牌中排第 ${br.position} 位(按被提及次数)`,
        };
  }
  const combined = rankWithBrand(report);
  if (!combined) return null;
  const ownIdx = combined.findIndex((r) => r.own);
  return {
    id: "rank",
    label: "竞品可见度位次",
    value: `${ownIdx + 1} / ${combined.length}`,
    sub: `在 ${combined.length} 个同台品牌中排第 ${ownIdx + 1} 位(按被提及次数)`,
  };
}

function buildKpis(report: GeoReport): HeroKpi[] {
  const ov = report.search_overview;
  const kpis: HeroKpi[] = [];

  // 01 AI 提及率
  if (ov?.valid_answers) {
    const rate = Math.round(((ov.mention_hits ?? 0) / ov.valid_answers) * 100);
    kpis.push({
      id: "mention",
      label: "AI 提及率",
      value: `${rate}%`,
      sub: `${ov.valid_answers} 个问句中被主动提及 ${ov.mention_hits ?? 0} 次`,
    });
  }

  // 02 引用信源域名数:优先 search_overview 全量统计(1005:citation_sources 只是 Top 列表,
  //    生产出现 10 域名/217 次 与 01 节 79/310 对不上);全量缺了才退到明细求和。
  const srcs = report.citation_sources ?? [];
  if (ov?.valid_domain_count) {
    kpis.push({
      id: "domains",
      label: "引用信源域名",
      value: String(ov.valid_domain_count),
      sub:
        ov.citation_url_count != null
          ? `联网检索共引用 ${ov.citation_url_count} 条链接`
          : "联网检索引用池统计",
    });
  } else if (srcs.length > 0) {
    const total = srcs.reduce((a, s) => a + s.count, 0);
    kpis.push({
      id: "domains",
      label: "引用信源域名",
      value: String(srcs.length),
      sub: `AI 回答共引用了这些域名 ${total} 次`,
    });
  }

  // 03 竞品可见度位次
  const rankKpi = computeCompetitorRankKpi(report);
  if (rankKpi) kpis.push(rankKpi);

  // 04 待修短板数(GeoShortboard 没有危险等级/预计提分字段,老实只报数量,不编)
  const boards = report.shortboards ?? [];
  if (boards.length > 0) {
    kpis.push({
      id: "shortboards",
      label: "待修短板",
      value: String(boards.length),
      sub: "按诊断建议逐条列出 · 详见下方板块",
    });
  }

  return kpis;
}

/**
 * 报告 Hero(照设计稿重排):深墨蓝带 —— 品牌大标题 + 总分大数字 + 评级药丸 + 较基线 delta +
 * meta 药丸;右上导出/基线动作组;带下压边 KPI 带(提及率/引用域名数/竞品位次/待修短板)。
 *
 * 「问题集依据」标注行(mockup 里 hero 下方那一行小字)**没有实现**:该信息来自发起体检时
 * 手填的 GeoRunInputs.brandHint,但 report_json / GeoRunDetail 都不透出这个原始入参
 * (ai 侧 derive_request 只把派生结果写进 report.brand/industry,零输入 vs 手填的痕迹
 * 没有留下来)。按硬约束「字段真的拿不到就不显示,禁止编」,这一行整体省略。
 */
export function ReportHero({
  report,
  createdAt,
  isBaseline,
  onExportHtml,
  onExportPdf,
  onRepointBaseline,
  repointing,
}: {
  report: GeoReport;
  createdAt?: string;
  isBaseline?: boolean;
  onExportHtml?: () => void;
  onExportPdf?: () => void;
  onRepointBaseline?: () => void;
  repointing?: boolean;
}) {
  const bd = report.score_breakdown ?? {};
  const overall = bd.overall ?? report.overall_score;
  const grade = bd.grade ?? report.grade;
  const realCount = report.platform_details?.real_count ?? 0;
  const ov = report.search_overview;

  const baselineOverallRow = report.baseline_compare?.available
    ? report.baseline_compare.rows?.find((r) => r.key === "overall")
    : undefined;
  const baselineDelta = baselineOverallRow?.delta ?? null;

  const kpis = buildKpis(report);

  return (
    <div className="space-y-3">
      <Card
        data-print-hero
        data-testid="geo-report-hero"
        className="relative overflow-hidden px-5 py-6 sm:px-8 sm:py-7"
      >
        {/* 装饰光斑(纯视觉,主题主色淡晕) */}
        <div
          aria-hidden
          className="pointer-events-none absolute -right-16 -top-24 h-72 w-72 rounded-full"
          style={{
            background: "radial-gradient(circle, hsl(var(--primary) / .14), transparent 65%)",
          }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-24 -left-10 h-60 w-60 rounded-full"
          style={{
            background: "radial-gradient(circle, hsl(var(--info) / .1), transparent 65%)",
          }}
        />

        <CardContent className="relative z-10 p-0">
          <HeroActions
            isBaseline={isBaseline}
            onExportHtml={onExportHtml}
            onExportPdf={onExportPdf}
            onRepointBaseline={onRepointBaseline}
            repointing={repointing}
          />

          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div className="flex max-w-xl flex-col gap-2.5">
              <div className="flex items-center gap-1.5 text-[12.5px] font-semibold tracking-[0.12em] text-primary">
                <Radar className="h-3.5 w-3.5" /> AI 可见度体检报告
              </div>
              <h1 className="text-3xl font-extrabold leading-tight sm:text-[40px]">
                {report.brand}
              </h1>
              <div className="flex flex-wrap gap-1.5">
                {report.industry && <HeroPill>{report.industry}</HeroPill>}
                {createdAt && <HeroPill>{fmtTime(createdAt)}</HeroPill>}
                {realCount > 0 && <HeroPill>{realCount} 引擎联网实测</HeroPill>}
                {ov?.question_count != null && (
                  <HeroPill>{ov.question_count} 问 · 逐条留证</HeroPill>
                )}
              </div>
            </div>

            <div className="flex items-end gap-6">
              <div className="flex flex-col gap-1">
                <div className="flex items-baseline gap-2.5">
                  <b
                    data-print-score
                    className="text-[56px] font-extrabold leading-none tabular-nums sm:text-[72px]"
                  >
                    {overall}
                  </b>
                  <div className="flex flex-col gap-1.5 pb-1">
                    <span className="inline-flex w-fit items-center rounded-full bg-success px-3 py-1 text-[14px] font-bold text-success-foreground">
                      {grade}
                    </span>
                    {baselineDelta != null &&
                      (roundDelta(baselineDelta) === 0 ? (
                        <span
                          data-testid="geo-hero-baseline-delta"
                          className="inline-flex items-center gap-1 text-[13px] font-semibold text-muted-foreground"
                        >
                          与基线轮持平
                        </span>
                      ) : (
                        <span
                          data-testid="geo-hero-baseline-delta"
                          className={`inline-flex items-center gap-1 text-[13px] font-semibold ${
                            baselineDelta > 0 ? "text-success" : "text-destructive"
                          }`}
                        >
                          {baselineDelta > 0 ? (
                            <ArrowUp className="h-3.5 w-3.5" />
                          ) : (
                            <ArrowDown className="h-3.5 w-3.5" />
                          )}
                          {formatScoreDelta(baselineDelta)} 较基线轮
                        </span>
                      ))}
                  </div>
                </div>
                <span className="text-[12.5px] text-muted-foreground">
                  AI 综合可见度 · 满分 100
                  {scoreVersionLabel(bd.score_version) && (
                    <>
                      {" "}
                      · <b>{scoreVersionLabel(bd.score_version)}</b>
                    </>
                  )}
                </span>
              </div>

              {(bd.components?.length ?? 0) > 0 && (
                <div className="hidden items-end gap-3 pb-1 sm:flex" data-testid="geo-hero-dims">
                  {bd.components!.map((c) => (
                    <div key={c.key} className="flex flex-col items-center gap-1.5">
                      <div
                        className="w-[18px] rounded-t bg-primary/80"
                        style={{ height: `${Math.max(6, c.score * 0.8)}px` }}
                      />
                      <span className="w-[38px] text-center text-[10.5px] leading-tight text-muted-foreground">
                        {c.label}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {kpis.length > 0 && (
        <div className="-mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="geo-kpi-band">
          {kpis.map((k) => (
            <HeroKpiCard key={k.id} kpi={k} />
          ))}
        </div>
      )}
    </div>
  );
}
